import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { guestApi } from '../api/client';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, TouchSensor,
  useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  verticalListSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Option { id: number; text: string; }
interface Question { id: number; text: string; options: Option[]; }

function SortableItem({ option, index }: { option: Option; index: number }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: option.id });
  const style = { transform: CSS.Transform.toString(transform), transition, touchAction: 'none' };
  return (
    <div
      ref={setNodeRef} style={style} {...attributes} {...listeners}
      className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow"
    >
      <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold flex-shrink-0">
        {index + 1}
      </span>
      <span className="text-gray-700">{option.text}</span>
    </div>
  );
}

export default function GuestQuiz() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [current, setCurrent] = useState(0);
  const [rankings, setRankings] = useState<{ [qid: number]: Option[] }>({});
  const [visited, setVisited] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    // 访客不需要登录,直接拉题目
    api.get('/api/questions').then(res => {
      setQuestions(res.data);
      const init: { [qid: number]: Option[] } = {};
      res.data.forEach((q: Question) => { init[q.id] = q.options; });
      setRankings(init);
      setLoading(false);
    });
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const q = questions[current];
    const items = rankings[q.id];
    const oldIndex = items.findIndex(o => o.id === active.id);
    const newIndex = items.findIndex(o => o.id === over.id);
    setRankings({ ...rankings, [q.id]: arrayMove(items, oldIndex, newIndex) });
  };

  const handlePrev = () => {
    if (current > 0) { setCurrent(current - 1); setSubmitError(''); }
  };

  const handleNext = () => {
    const currentQId = questions[current].id;
    const newVisited = new Set(visited);
    newVisited.add(currentQId);
    setVisited(newVisited);
    if (current < questions.length - 1) {
      setCurrent(current + 1);
      setSubmitError('');
    } else {
      handleSubmit(newVisited);
    }
  };

  const handleSubmit = async (visitedSet: Set<number>) => {
    const allVisited = questions.every(q => visitedSet.has(q.id));
    if (!allVisited) { setSubmitError('请确认每道题都已完成排序'); return; }

    setSubmitting(true);
    setSubmitError('');
    const payload = Object.entries(rankings).map(([qid, opts]) => ({
      question_id: parseInt(qid),
      option_rankings: opts.map(o => o.id),
    }));
    try {
      const res = await guestApi.score(payload);
      // 结果不落库,直接带进结果页(内存传递,刷新即失)
      navigate('/guest-result', { state: res.data });
    } catch (e: any) {
      setSubmitError(e.response?.data?.detail || '提交失败,请重试');
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500">加载中...</p>
    </div>
  );

  const q = questions[current];
  const currentOptions = rankings[q.id] || q.options;
  const isFirst = current === 0;
  const isLast = current === questions.length - 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 w-full max-w-lg">
        <div className="mb-2">
          <span className="inline-block text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">自由体验模式</span>
        </div>
        <div className="flex justify-between items-center mb-6">
          <span className="text-sm text-gray-500">第 {current + 1} 题 / 共 {questions.length} 题</span>
          <div className="flex gap-1">
            {questions.map((qq, i) => {
              const isDone = visited.has(qq.id);
              const isCurrent = i === current;
              let className = 'h-2 w-6 rounded-full ';
              if (isCurrent) className += 'bg-indigo-600 ring-2 ring-indigo-300';
              else if (isDone) className += 'bg-indigo-400';
              else className += 'bg-gray-200';
              return <div key={i} className={className} />;
            })}
          </div>
        </div>

        <h2 className="text-lg font-semibold text-gray-800 mb-2">{q.text}</h2>
        <p className="text-sm text-gray-400 mb-6">
          请将以下选项从最符合你到最不符合你的顺序排列(拖拽调整)
          <br /><span className="text-xs">💡 手机:长按选项后拖动</span>
        </p>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={currentOptions.map(o => o.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3 mb-6">
              {currentOptions.map((opt, idx) => (
                <SortableItem key={opt.id} option={opt} index={idx} />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        {submitError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-600 text-sm">{submitError}</p>
          </div>
        )}

        <div className="flex gap-3">
          <button
            onClick={handlePrev} disabled={isFirst || submitting}
            className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >← 上一题</button>
          <button
            onClick={handleNext} disabled={submitting}
            className={`flex-1 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 ${
              isLast ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700'
            }`}
          >{submitting ? '计算中...' : isLast ? '查看结果 ✓' : '下一题 →'}</button>
        </div>

        <p className="text-xs text-gray-400 text-center mt-4">
          💡 自由体验模式的结果仅供参考,不会保存,也不参与书院分配
        </p>
      </div>
    </div>
  );
}
